/**
 * Generic document restructuring ops for the agent.
 *
 * Unlike `AgentStructureApplier`, which only *adds* formatting to existing paragraphs,
 * this module supports **mutating the document structure**: deleting paragraphs,
 * editing paragraph text in place, promoting / demoting outline levels of existing
 * list items, splitting and merging paragraphs, and inserting new paragraphs at
 * arbitrary anchor positions.
 *
 * All ops address paragraphs by the **initial** agent paragraph index (0-based over
 * non-empty paragraphs, matching `DocumentReader.getFullDocumentContext()`). Index
 * stability across a batch is guaranteed by snapshotting `Word.Paragraph` object
 * references up front — subsequent ops in the same batch never see shifted indices,
 * even after deletes, inserts, or splits performed by earlier ops.
 */

/* global Word */

import { log, logError } from '../../utils/logging';
import { formatOfficeJsError } from '../../utils/officeErrorFormat';

/**
 * Strip leading list/outline numbers from a paragraph text before it's written back.
 *
 * The LLM sometimes types outline numbers ("3.7.", "3.8.", "1.1.", "IV.") into the
 * replacement text even though Word already numbers the paragraph via its list style.
 * If we write those back verbatim we get double numbering (e.g. "3.15.1  3.7. סעיף 5.3-").
 *
 * This helper removes ONE leading numeric/outline token if it looks like a list number
 * (digits + dots, optionally followed by a period/paren/dash and whitespace). It is
 * conservative: it only strips when the paragraph WILL be re-emitted inside a Word list
 * (i.e. the caller says the paragraph is a list item).
 */
export function stripLeadingListNumber(text: string, paragraphIsListItem: boolean): string {
  if (!paragraphIsListItem) return text;
  const raw = text ?? '';
  // Match: optional RTL/LTR marks, then digits(.digits)* optionally with a trailing dot,
  // OR a single letter followed by "." (a., A., i., I.), followed by separator ")", ".",
  // "-", or whitespace. Only strip if what follows still contains real content.
  const pattern =
    /^[\s\u200e\u200f\u061c]*(?:\d+(?:\.\d+)*\.?|[A-Za-z]{1,3}\.)[)\.\-\s]+/u;
  const stripped = raw.replace(pattern, '');
  // Guard: never strip everything — if the entire paragraph was just a number, keep original.
  if (!stripped.trim()) return raw;
  if (stripped !== raw) {
    log('[DocumentRestructurer] stripped leading list number', {
      before: raw.slice(0, 40),
      after: stripped.slice(0, 40),
    });
  }
  return stripped;
}

async function syncWithLabel(context: Word.RequestContext, label: string): Promise<void> {
  try {
    await context.sync();
    log('[DocumentRestructurer] sync OK', label);
  } catch (e: unknown) {
    logError('[DocumentRestructurer] sync FAILED', label, formatOfficeJsError(e));
    throw e;
  }
}

export type RestructureOp =
  | { op: 'delete'; agent_paragraph_index: number }
  | { op: 'replace_text'; agent_paragraph_index: number; text: string }
  | {
      op: 'set_list_level';
      agent_paragraph_index: number;
      /** 0 = top level (1., 2., …), 1 = 1.1, 1.2, …, up to 8 */
      level: number;
    }
  | { op: 'detach_from_list'; agent_paragraph_index: number }
  | {
      /** Attach paragraph i to the same Word list that paragraph `into_agent_paragraph_index` belongs to. */
      op: 'join_list';
      agent_paragraph_index: number;
      into_agent_paragraph_index: number;
      level?: number;
    }
  | {
      /** Split paragraph at the beginning of `before_substring` (case-sensitive) into two paragraphs. */
      op: 'split';
      agent_paragraph_index: number;
      before_substring: string;
    }
  | {
      /** Merge paragraph with the paragraph immediately following it (in document order). */
      op: 'merge_with_next';
      agent_paragraph_index: number;
      separator?: string;
    }
  | {
      /** Promote a sub-clause to the same outline level as its parent (e.g. 3.7.1 → sibling 3.8). */
      op: 'promote_sub_clause';
      /** The sub-clause paragraph to promote. */
      agent_paragraph_index: number;
      /** Parent section paragraph (defines target list level). */
      parent_agent_paragraph_index: number;
      /** If set, replaces paragraph text entirely. Otherwise keeps existing text. */
      text?: string;
      /** If true, prepend the parent's body text before the sub-clause text (before any `text` replace). */
      prepend_parent_text?: boolean;
    }
  | {
      op: 'insert_after';
      after_agent_paragraph_index: number;
      text: string;
      align?: 'Left' | 'Centered' | 'Right' | 'Justified';
      bold?: boolean;
      /** Optionally join the newly inserted paragraph to the same list as another paragraph. */
      join_list_of_agent_paragraph_index?: number;
      level?: number;
    }
  | {
      /**
       * Deterministic flatten: given ONE parent paragraph index, the client discovers all direct
       * children (contiguous list items at deeper levels, until a paragraph at ≤ parent level
       * appears), promotes each child to the parent's level, prepends the parent's own text
       * (stripped of list numbering) to each child's text, then deletes the parent.
       *
       * The LLM should use this instead of `set_list_level` / `replace_text` / `promote_sub_clause`
       * combinations for "flatten sub-clauses" requests — the client picks the correct level and
       * the correct child indices, so the LLM cannot corrupt content or use the wrong outline level.
       */
      op: 'flatten_sub_clauses';
      parent_agent_paragraph_index: number;
      /**
       * If true (default), prepend the parent's non-numbered body text to each child before
       * promotion. If false, leave child text as-is (structure change only).
       */
      prepend_parent_text?: boolean;
    };

type Anchor = {
  para: Word.Paragraph | null;
  deleted: boolean;
};

async function buildInitialAnchors(context: Word.RequestContext): Promise<Anchor[]> {
  const body = context.document.body;
  const pc = body.paragraphs;
  pc.load('items');
  await syncWithLabel(context, 'restructure: load body.paragraphs.items');
  const items = pc.items as unknown as Word.Paragraph[];
  for (const p of items) p.load(['text', 'isListItem']);
  await syncWithLabel(context, 'restructure: load paragraph text+isListItem');

  const anchors: Anchor[] = [];
  for (const p of items) {
    const text = String((p as unknown as { text: string }).text ?? '');
    if (text.trim().length === 0) continue;
    anchors.push({ para: p, deleted: false });
  }
  log('[DocumentRestructurer] initial anchors', { count: anchors.length });
  return anchors;
}

function requireAnchor(anchors: Anchor[], agentIdx: number, opLabel: string): Word.Paragraph {
  const a = anchors[agentIdx];
  if (!a || a.deleted || !a.para) {
    throw new Error(`${opLabel}: agent paragraph ${agentIdx} not available (deleted or out of range)`);
  }
  return a.para;
}

/** Attach `para` to the same Word list as `listSource`, at `level` (defaults to listSource's level). */
async function attachToSameListAs(
  context: Word.RequestContext,
  label: string,
  para: Word.Paragraph,
  listSource: Word.Paragraph,
  level?: number
): Promise<void> {
  listSource.load(['isListItem', 'listItem', 'listOrNullObject']);
  para.load('isListItem');
  await syncWithLabel(context, `${label} load list source`);
  if (!listSource.isListItem) {
    throw new Error(`${label}: list source paragraph is not a list item`);
  }
  const list = listSource.listOrNullObject;
  list.load('id');
  await syncWithLabel(context, `${label} load list id`);
  const listId = list.id;
  if (listId == null) {
    throw new Error(`${label}: could not resolve list id`);
  }
  const srcLevel = listSource.listItem.level ?? 0;
  const lvl =
    level != null ? Math.max(0, Math.min(8, Number(level))) : Math.max(0, Math.min(8, srcLevel));
  if (para.isListItem) {
    para.detachFromList();
    await syncWithLabel(context, `${label} detach before attach`);
  }
  para.attachToList(listId, lvl);
  await syncWithLabel(context, `${label} attachToList level=${lvl}`);
}

export async function applyDocumentRestructureOps(ops: RestructureOp[]): Promise<{
  ok: boolean;
  /** Number of ops that performed a real mutation on the document. */
  applied: number;
  /** Number of ops that were safely skipped (no-op) without failing the batch. */
  skipped: number;
  /** Number of ops that threw an error but were isolated from the rest of the batch. */
  failed: number;
  detail?: string;
}> {
  if (!Array.isArray(ops) || ops.length === 0) {
    return { ok: false, applied: 0, skipped: 0, failed: 0, detail: 'No operations' };
  }

  try {
    log('[DocumentRestructurer] start', {
      opCount: ops.length,
      opsPreview: ops.slice(0, 12).map((o, i) => ({
        i,
        op: (o as RestructureOp)?.op,
        keys: o && typeof o === 'object' ? Object.keys(o as object) : [],
      })),
    });

    let applied = 0;
    let skipped = 0;
    let failed = 0;

    await Word.run(async (context) => {
      const anchors = await buildInitialAnchors(context);

      for (let opIdx = 0; opIdx < ops.length; opIdx++) {
        const raw = ops[opIdx];
        if (!raw || typeof raw !== 'object' || !('op' in raw)) continue;
        const op = raw as RestructureOp;
        const label = `op${opIdx + 1}:${op.op}`;
        log('[DocumentRestructurer] op begin', { opIdx, op: op.op });
        // Per-op error isolation: if one op throws (e.g. wrong index, list metadata
        // mismatch), record it and continue with the next op. Aborting the whole
        // batch was wasting all the other legitimate ops in the same request.
        try {

        if (op.op === 'delete') {
          const p = requireAnchor(anchors, op.agent_paragraph_index, label);
          p.delete();
          anchors[op.agent_paragraph_index].deleted = true;
          anchors[op.agent_paragraph_index].para = null;
          await syncWithLabel(context, `${label} delete`);
          applied += 1;
          continue;
        }

        if (op.op === 'replace_text') {
          const p = requireAnchor(anchors, op.agent_paragraph_index, label);
          p.load('isListItem');
          await syncWithLabel(context, `${label} probe isListItem`);
          const cleaned = stripLeadingListNumber(String(op.text ?? ''), !!p.isListItem);
          p.getRange().insertText(cleaned, Word.InsertLocation.replace);
          await syncWithLabel(context, `${label} replace_text`);
          applied += 1;
          continue;
        }

        if (op.op === 'detach_from_list') {
          const p = requireAnchor(anchors, op.agent_paragraph_index, label);
          p.load('isListItem');
          await syncWithLabel(context, `${label} load isListItem`);
          if (p.isListItem) {
            p.detachFromList();
            await syncWithLabel(context, `${label} detachFromList`);
          }
          applied += 1;
          continue;
        }

        if (op.op === 'set_list_level') {
          const p = requireAnchor(anchors, op.agent_paragraph_index, label);
          const lvl = Math.max(0, Math.min(8, Number(op.level) || 0));
          p.load(['isListItem', 'listItem']);
          await syncWithLabel(context, `${label} load listItem`);
          if (!p.isListItem) {
            throw new Error(
              `${label}: paragraph ${op.agent_paragraph_index} is not a list item; cannot change level.`
            );
          }
          p.listItem.level = lvl;
          await syncWithLabel(context, `${label} listItem.level=${lvl}`);
          applied += 1;
          continue;
        }

        if (op.op === 'join_list') {
          const p = requireAnchor(anchors, op.agent_paragraph_index, label);
          const target = requireAnchor(anchors, op.into_agent_paragraph_index, label);
          const lvl = op.level != null ? Math.max(0, Math.min(8, Number(op.level))) : undefined;
          await attachToSameListAs(context, label, p, target, lvl);
          applied += 1;
          continue;
        }

        if (op.op === 'promote_sub_clause') {
          const sub = requireAnchor(anchors, op.agent_paragraph_index, label);
          const parent = requireAnchor(anchors, op.parent_agent_paragraph_index, label);
          sub.load(['text', 'isListItem', 'listItem']);
          parent.load(['text', 'isListItem', 'listItem']);
          await syncWithLabel(context, `${label} load sub+parent`);

          if (!sub.isListItem) {
            throw new Error(
              `${label}: paragraph ${op.agent_paragraph_index} is not a list item; cannot promote.`
            );
          }

          let newText: string | undefined =
            op.text !== undefined && op.text !== null ? String(op.text) : undefined;
          if (newText === undefined && op.prepend_parent_text) {
            const parentText = stripLeadingListNumber(
              String(parent.text ?? '').trim(),
              !!parent.isListItem
            );
            const subText = stripLeadingListNumber(
              String(sub.text ?? '').trim(),
              !!sub.isListItem
            );
            newText = parentText ? `${parentText} ${subText}`.trim() : subText;
          }
          if (newText !== undefined) {
            // Always strip any LLM-typed outline number since the paragraph stays in a Word list.
            const cleaned = stripLeadingListNumber(newText, true);
            sub.getRange().insertText(cleaned, Word.InsertLocation.replace);
            await syncWithLabel(context, `${label} replace text for promote`);
          }

          // Warn (do not fail) if the model picked a bogus parent — parent's level should be
          // STRICTLY LESS than the sub-clause's current level. If not, promotion will be a no-op
          // and the caller sees a signal in the logs to retry with a better parent index.
          if (parent.isListItem && sub.isListItem) {
            parent.listItem.load('level');
            sub.listItem.load('level');
            await syncWithLabel(context, `${label} probe parent vs sub level`);
            const pLvl = parent.listItem.level ?? 0;
            const sLvl = sub.listItem.level ?? 0;
            if (pLvl >= sLvl) {
              log('[DocumentRestructurer] WARN parent not above sub-clause', {
                agent_paragraph_index: op.agent_paragraph_index,
                parent_agent_paragraph_index: op.parent_agent_paragraph_index,
                parent_level: pLvl,
                sub_level: sLvl,
              });
            }
          }

          // Target level = parent's outline level (NOT hardcoded 0). E.g. 3.7.1 at level 2
          // under parent 3.7 at level 1 → promoted item becomes level 1 (shows as 3.8).
          let targetLevel = 0;
          if (parent.isListItem) {
            parent.listItem.load('level');
            await syncWithLabel(context, `${label} load parent level`);
            targetLevel = Math.max(0, Math.min(8, parent.listItem.level ?? 0));
          }
          sub.listItem.level = targetLevel;
          await syncWithLabel(context, `${label} promote listItem.level=${targetLevel}`);
          applied += 1;
          continue;
        }

        if (op.op === 'split') {
          const p = requireAnchor(anchors, op.agent_paragraph_index, label);
          const marker = String(op.before_substring ?? '');
          if (!marker) throw new Error(`${label}: before_substring is required`);
          const range = p.getRange();
          range.load('text');
          await syncWithLabel(context, `${label} load range text`);
          const text = String(range.text ?? '');
          const pos = text.indexOf(marker);
          if (pos < 0) {
            throw new Error(`${label}: split marker not found in paragraph ${op.agent_paragraph_index}`);
          }
          const before = text.slice(0, pos).replace(/\s+$/, '');
          const after = text.slice(pos);
          p.getRange().insertText(before, Word.InsertLocation.replace);
          const newPara = p.insertParagraph(after, Word.InsertLocation.after);
          await syncWithLabel(context, `${label} split executed`);
          // The new paragraph is not addressable by the original agent-index scheme;
          // it can be joined into a list in a subsequent op if needed — we do not
          // expose a synthetic index for it here to keep the batch contract simple.
          void newPara;
          applied += 1;
          continue;
        }

        if (op.op === 'merge_with_next') {
          const p = requireAnchor(anchors, op.agent_paragraph_index, label);
          // Find the next non-deleted anchor after this one:
          let nextIdx = -1;
          for (let j = op.agent_paragraph_index + 1; j < anchors.length; j++) {
            if (!anchors[j].deleted && anchors[j].para) { nextIdx = j; break; }
          }
          if (nextIdx < 0) throw new Error(`${label}: no following paragraph to merge`);
          const nextPara = anchors[nextIdx].para as Word.Paragraph;
          const sep = op.separator ?? ' ';
          const nRange = nextPara.getRange();
          nRange.load('text');
          await syncWithLabel(context, `${label} load next range text`);
          const nText = String(nRange.text ?? '');
          const pRange = p.getRange();
          pRange.load('text');
          await syncWithLabel(context, `${label} load current range text`);
          const pText = String(pRange.text ?? '');
          p.getRange().insertText(pText + sep + nText, Word.InsertLocation.replace);
          nextPara.delete();
          anchors[nextIdx].deleted = true;
          anchors[nextIdx].para = null;
          await syncWithLabel(context, `${label} merged and deleted next`);
          applied += 1;
          continue;
        }

        if (op.op === 'insert_after') {
          const anchor = requireAnchor(anchors, op.after_agent_paragraph_index, label);
          const newPara = anchor.insertParagraph(String(op.text ?? ''), Word.InsertLocation.after);
          if (op.align) newPara.alignment = op.align;
          if (op.bold) newPara.font.bold = true;
          await syncWithLabel(context, `${label} insert paragraph`);

          // Join to list: default to anchor paragraph's list at anchor's level.
          const joinIdx =
            op.join_list_of_agent_paragraph_index ?? op.after_agent_paragraph_index;
          const listSource =
            joinIdx === op.after_agent_paragraph_index
              ? anchor
              : requireAnchor(anchors, joinIdx, label);
          const lvl =
            op.level != null ? Math.max(0, Math.min(8, Number(op.level))) : undefined;
          listSource.load(['isListItem', 'listItem']);
          await syncWithLabel(context, `${label} probe list source`);
          if (listSource.isListItem) {
            await attachToSameListAs(context, label, newPara, listSource, lvl);
          }
          await syncWithLabel(context, `${label} insert_after done`);
          applied += 1;
          continue;
        }

        if (op.op === 'flatten_sub_clauses') {
          // The model often mis-identifies the parent — Word list numbers are auto-generated
          // and not visible in paragraph.text, so it can't reliably tell a header from a
          // sub-clause. We auto-adjust: if the given index has no deeper children, walk
          // backwards to find the closest list item at a strictly lower level (the real
          // parent header) and use that instead.
          let parentIdx = op.parent_agent_paragraph_index;
          // Gracefully skip if this paragraph was already flattened by a prior op in this batch
          // (multiple ops in the same batch may auto-adjust to the same real parent).
          const initialAnchor = anchors[parentIdx];
          if (!initialAnchor || initialAnchor.deleted || !initialAnchor.para) {
            log('[DocumentRestructurer] flatten_sub_clauses skip (parent already gone)', {
              requestedParent: parentIdx,
            });
            skipped += 1;
            continue;
          }
          let parent = requireAnchor(anchors, parentIdx, label);
          parent.load(['text', 'isListItem']);
          await syncWithLabel(context, `${label} load initialparent`);
          if (!parent.isListItem) {
            log('[DocumentRestructurer] flatten_sub_clauses skip (parent not a list item)', {
              requestedParent: parentIdx,
              text: String(parent.text ?? '').slice(0, 80).trim(),
            });
            skipped += 1;
            continue;
          }
          parent.listItem.load('level');
          await syncWithLabel(context, `${label} load initial parent level`);
          const initialLevel = Math.max(0, Math.min(8, parent.listItem.level ?? 0));

          // Peek at the next non-deleted anchor to decide if this paragraph is already the
          // real parent (next is deeper) or a sub-clause (next is same/higher level).
          let needsBackWalk = false;
          for (let j = parentIdx + 1; j < anchors.length; j++) {
            const a = anchors[j];
            if (!a || a.deleted || !a.para) continue;
            const nextP = a.para;
            nextP.load('isListItem');
            await syncWithLabel(context, `${label} peek next ${j}`);
            if (!nextP.isListItem) {
              needsBackWalk = true;
              break;
            }
            nextP.listItem.load('level');
            await syncWithLabel(context, `${label} peek next level ${j}`);
            const nextLvl = nextP.listItem.level ?? 0;
            if (nextLvl <= initialLevel) needsBackWalk = true;
            break;
          }

          if (needsBackWalk && initialLevel > 0) {
            // SAFETY: only walk back a short distance and require the target to be EXACTLY
            // one level shallower (initialLevel - 1). Walking further or landing on a
            // top-level document heading catastrophically flattens unrelated sections.
            const MAX_BACK_WALK = 3;
            const requiredParentLevel = initialLevel - 1;
            let foundRealParent = -1;
            let stepsWalked = 0;
            for (let k = parentIdx - 1; k >= 0 && stepsWalked < MAX_BACK_WALK; k--) {
              const a = anchors[k];
              if (!a || a.deleted || !a.para) continue;
              stepsWalked += 1;
              const cand = a.para;
              cand.load('isListItem');
              await syncWithLabel(context, `${label} back-walk isListItem ${k}`);
              if (!cand.isListItem) break;
              cand.listItem.load('level');
              await syncWithLabel(context, `${label} back-walk level ${k}`);
              const candLvl = cand.listItem.level ?? 0;
              if (candLvl === requiredParentLevel) {
                foundRealParent = k;
                break;
              }
              if (candLvl < requiredParentLevel) {
                // We passed the level we wanted — don't use it, safer to skip.
                break;
              }
            }
            if (foundRealParent < 0) {
              const initialParent = anchors[op.parent_agent_paragraph_index]?.para;
              let requestedText = '';
              if (initialParent) {
                initialParent.load('text');
                await syncWithLabel(context, `${label} log requested text`);
                requestedText = String(initialParent.text ?? '').slice(0, 80).trim();
              }
              log('[DocumentRestructurer] flatten_sub_clauses skip (no real parent within back-walk range)', {
                requestedParent: op.parent_agent_paragraph_index,
                initialLevel,
                requestedText,
              });
              skipped += 1;
              continue;
            }
            log('[DocumentRestructurer] flatten_sub_clauses auto-adjusted parent', {
              from: parentIdx,
              fromLevel: initialLevel,
              to: foundRealParent,
            });
            parentIdx = foundRealParent;
            const adjustedAnchor = anchors[parentIdx];
            if (!adjustedAnchor || adjustedAnchor.deleted || !adjustedAnchor.para) {
              log('[DocumentRestructurer] flatten_sub_clauses skip (adjusted parent already processed)', {
                parent: parentIdx,
              });
              skipped += 1;
              continue;
            }
            parent = requireAnchor(anchors, parentIdx, label);
            parent.load(['text', 'isListItem']);
            await syncWithLabel(context, `${label} load adjusted parent`);
            parent.listItem.load('level');
            await syncWithLabel(context, `${label} load adjusted parent level`);
          }

          const parentLevel = Math.max(0, Math.min(8, parent.listItem.level ?? 0));
          const parentTextRaw = String(parent.text ?? '').trim();
          const parentTextClean = stripLeadingListNumber(parentTextRaw, true);

          const childIndices: number[] = [];
          // Walk candidates one at a time — need two syncs per candidate because
          // Paragraph.listItem throws ItemNotFound if the paragraph isn't a list item,
          // so we must check isListItem in a separate round before touching .listItem.
          for (let j = parentIdx + 1; j < anchors.length; j++) {
            const a = anchors[j];
            if (!a || a.deleted || !a.para) continue;
            const p = a.para;
            p.load('isListItem');
            await syncWithLabel(context, `${label} probe isListItem ${j}`);
            if (!p.isListItem) break;
            p.listItem.load('level');
            await syncWithLabel(context, `${label} probe level ${j}`);
            const lvl = p.listItem.level ?? 0;
            if (lvl <= parentLevel) break;
            childIndices.push(j);
          }

          log('[DocumentRestructurer] flatten_sub_clauses discovered', {
            parent: parentIdx,
            requestedParent: op.parent_agent_paragraph_index,
            parentLevel,
            childCount: childIndices.length,
            childIndices,
          });

          if (childIndices.length === 0) {
            parent.load('text');
            await syncWithLabel(context, `${label} log no-children text`);
            log('[DocumentRestructurer] flatten_sub_clauses skip (no children)', {
              parent: parentIdx,
              parentLevel,
              parentText: String(parent.text ?? '').slice(0, 80).trim(),
            });
            skipped += 1;
            continue;
          }

          // SAFETY: refuse to flatten unreasonably large groups. Realistic patterns have
          // 2-8 sub-clauses; anything larger is almost certainly a wrong parent match
          // (e.g. auto-adjust landed on a top-level heading, so ALL sections became "children").
          // Soft-skip rather than throwing — throwing would abort the entire batch and take
          // down other, legitimate flatten ops with it.
          const MAX_CHILDREN_PER_FLATTEN = 12;
          if (childIndices.length > MAX_CHILDREN_PER_FLATTEN) {
            log('[DocumentRestructurer] flatten_sub_clauses skip (too many children — parent likely wrong)', {
              parent: parentIdx,
              requestedParent: op.parent_agent_paragraph_index,
              parentLevel,
              childCount: childIndices.length,
              cap: MAX_CHILDREN_PER_FLATTEN,
            });
            skipped += 1;
            continue;
          }

          const prependParent = op.prepend_parent_text !== false;

          for (const cIdx of childIndices) {
            const child = anchors[cIdx].para as Word.Paragraph;
            child.load(['text', 'listItem']);
            await syncWithLabel(context, `${label} load child ${cIdx}`);
            const childTextRaw = String(child.text ?? '').trim();
            const childTextClean = stripLeadingListNumber(childTextRaw, true);
            const newText = prependParent && parentTextClean
              ? `${parentTextClean} ${childTextClean}`.trim()
              : childTextClean;
            child.getRange().insertText(newText, Word.InsertLocation.replace);
            child.listItem.level = parentLevel;
            await syncWithLabel(
              context,
              `${label} promote child ${cIdx} → level=${parentLevel}`
            );
          }

          parent.delete();
          anchors[parentIdx].deleted = true;
          anchors[parentIdx].para = null;
          await syncWithLabel(context, `${label} delete parent header`);
          applied += 1;
          continue;
        }

        throw new Error(`${label}: unknown op '${(op as { op: string }).op}'`);
        } catch (opErr: unknown) {
          const opMsg = opErr instanceof Error ? opErr.message : String(opErr);
          failed += 1;
          logError('[DocumentRestructurer] op failed (continuing)', {
            opIdx,
            op: op.op,
            msg: opMsg,
            office: formatOfficeJsError(opErr),
          });
          // Best-effort: sync after a failed op so subsequent ops start from a clean
          // request queue; ignore secondary errors here.
          try {
            await context.sync();
          } catch {
            // swallow — the outer loop will surface any fatal issue on the next op
          }
          continue;
        }
      }
    });

    const ok = failed === 0;
    log('[DocumentRestructurer] applied', { steps: applied, skipped, failed });
    return { ok, applied, skipped, failed, detail: failed > 0 ? `${failed} op(s) failed` : undefined };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    logError('[DocumentRestructurer] failed', { msg, office: formatOfficeJsError(e) });
    return { ok: false, applied: 0, skipped: 0, failed: 0, detail: msg };
  }
}
